import React, { useState } from "react";
import { Grid, Typography, FormControl, InputLabel, Select, MenuItem, TextField, RadioGroup, FormControlLabel, Radio, FormLabel } from "@mui/material";
import { Translate } from "react-localize-redux";
import { Alert } from "@mui/lab";
import Modal from "../../../components/general/modal";
import { ButtonCancel, ButtonContinue } from "../../../components/general/mini_components";
import { PatientStatus } from "../../../constants/types";

interface DeathData {
    dateOfDeath: Date;
    timeOfDeath?: string;
    placeOfDeath?: string;
    deathConfirmedBy?: string;
    immediateCause: string;
    underlyingCause?: string;
    palliativePathway?: boolean;
    cause?: string; // legacy compat
}

interface StatusChangeModalProps {
    open: boolean;
    currentStatus: PatientStatus;
    patientName: string;
    onClose: () => void;
    onConfirm: (newStatus: number, deathData?: DeathData) => void;
}

const PLACE_OPTIONS = ["HOME", "HOSPICE", "HOSPITAL", "OTHER", "UNKNOWN"] as const;
const CONFIRMED_BY_OPTIONS = ["PHYSICIAN", "NURSE", "FAMILY"] as const;

const emptyDeathForm = {
    dateOfDeath: "",
    timeOfDeath: "",
    placeOfDeath: "",
    deathConfirmedBy: "",
    immediateCause: "",
    underlyingCause: "",
    palliativePathway: "" as "" | "yes" | "no",
};

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
    open, currentStatus, patientName, onClose, onConfirm
}) => {
    const [selectedStatus, setSelectedStatus] = useState<number | "">("");
    const [deathFormData, setDeathFormData] = useState(emptyDeathForm);

    function handleClose() {
        setSelectedStatus("");
        setDeathFormData(emptyDeathForm);
        onClose();
    }

    function handleConfirm() {
        if (selectedStatus === "") return;

        if (selectedStatus === PatientStatus.DECEASED) {
            if (!deathFormData.dateOfDeath || !deathFormData.immediateCause) return;
            onConfirm(selectedStatus, {
                dateOfDeath: new Date(deathFormData.dateOfDeath),
                timeOfDeath: deathFormData.timeOfDeath || undefined,
                placeOfDeath: deathFormData.placeOfDeath || undefined,
                deathConfirmedBy: deathFormData.deathConfirmedBy || undefined,
                immediateCause: deathFormData.immediateCause,
                cause: deathFormData.immediateCause, // legacy compat
                underlyingCause: deathFormData.underlyingCause || undefined,
                palliativePathway: deathFormData.palliativePathway === "yes" ? true : deathFormData.palliativePathway === "no" ? false : undefined,
            });
        } else {
            onConfirm(selectedStatus);
        }
        handleClose();
    }

    function updateField(field: string, value: string) {
        setDeathFormData(prev => ({ ...prev, [field]: value }));
    }

    const isDeceased = currentStatus === PatientStatus.DECEASED;

    return (
        <Modal open={open} closeModal={handleClose}>
            <Grid container spacing={2} style={{ padding: "1rem", maxWidth: 500 }}>
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        <Translate id="patient.status.change" />
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        {patientName}
                    </Typography>
                </Grid>
                {isDeceased ? (
                    <Grid item xs={12}>
                        <Alert severity="error">
                            <Translate id="patient.status.record-locked" />
                        </Alert>
                    </Grid>
                ) : (
                    <>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel><Translate id="patient.status.title" /></InputLabel>
                                <Select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value as number)}
                                    data-testid="status-change-select"
                                >
                                    {currentStatus !== PatientStatus.ACTIVE && (
                                        <MenuItem value={PatientStatus.ACTIVE}>
                                            <Translate id="patient.status.active" />
                                        </MenuItem>
                                    )}
                                    {currentStatus !== PatientStatus.INACTIVE && (
                                        <MenuItem value={PatientStatus.INACTIVE}>
                                            <Translate id="patient.status.inactive" />
                                        </MenuItem>
                                    )}
                                    <MenuItem value={PatientStatus.DECEASED}>
                                        <Translate id="patient.status.deceased" />
                                    </MenuItem>
                                    {currentStatus !== PatientStatus.ARCHIVED && (
                                        <MenuItem value={PatientStatus.ARCHIVED}>
                                            <Translate id="patient.status.archived" />
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        {selectedStatus === PatientStatus.DECEASED && (
                            <>
                                <Grid item xs={12}>
                                    <Alert severity="warning" style={{ marginBottom: "0.5rem" }}>
                                        <Translate id="patient.status.confirm-deceased" />
                                    </Alert>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label={<Translate id="patient.death.date" />}
                                        value={deathFormData.dateOfDeath}
                                        onChange={(e) => updateField("dateOfDeath", e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        data-testid="status-death-date"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="time"
                                        label={<Translate id="patient.death.time" />}
                                        value={deathFormData.timeOfDeath}
                                        onChange={(e) => updateField("timeOfDeath", e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        data-testid="status-death-time"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">
                                            <Translate id="patient.death.place" />
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={deathFormData.placeOfDeath}
                                            onChange={(e) => updateField("placeOfDeath", e.target.value)}
                                            data-testid="status-death-place"
                                        >
                                            {PLACE_OPTIONS.map(val => (
                                                <FormControlLabel
                                                    key={val}
                                                    value={val}
                                                    control={<Radio size="small" />}
                                                    label={<Translate id={`patient.death.place-${val.toLowerCase()}`} />}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">
                                            <Translate id="patient.death.confirmed-by" />
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={deathFormData.deathConfirmedBy}
                                            onChange={(e) => updateField("deathConfirmedBy", e.target.value)}
                                            data-testid="status-death-confirmed-by"
                                        >
                                            {CONFIRMED_BY_OPTIONS.map(val => (
                                                <FormControlLabel
                                                    key={val}
                                                    value={val}
                                                    control={<Radio size="small" />}
                                                    label={<Translate id={`patient.death.confirmed-by-${val.toLowerCase()}`} />}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={<Translate id="patient.death.immediate-cause" />}
                                        value={deathFormData.immediateCause}
                                        onChange={(e) => updateField("immediateCause", e.target.value)}
                                        required
                                        data-testid="status-death-immediate-cause"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label={<Translate id="patient.death.underlying-cause" />}
                                        value={deathFormData.underlyingCause}
                                        onChange={(e) => updateField("underlyingCause", e.target.value)}
                                        data-testid="status-death-underlying-cause"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">
                                            <Translate id="patient.death.palliative-pathway" />
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={deathFormData.palliativePathway}
                                            onChange={(e) => updateField("palliativePathway", e.target.value)}
                                            data-testid="status-death-palliative"
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" />} label={<Translate id="general.yes" />} />
                                            <FormControlLabel value="no" control={<Radio size="small" />} label={<Translate id="general.no" />} />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} style={{ paddingTop: "1rem" }}>
                            <ButtonCancel onClick={handleClose} data-testid="cancel-status-change" color="primary" spaceright={1}>
                                <Translate id="general.cancel" />
                            </ButtonCancel>
                            &nbsp;
                            <ButtonContinue
                                onClick={handleConfirm}
                                data-testid="confirm-status-change"
                                color="green"
                                disabled={selectedStatus === ""}
                            >
                                <Translate id="general.confirm" />
                            </ButtonContinue>
                        </Grid>
                    </>
                )}
            </Grid>
        </Modal>
    );
};

export default StatusChangeModal;
